import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export interface TimeSlot {
  start_time: string;
  end_time: string;
  day_of_week: number;
  specific_date: string | null;
  timezone?: string;
}

export function useAvailabilityManagement(user: any) {
  const [loading, setLoading] = useState(false);
  const [availabilities, setAvailabilities] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const createAvailability = async (
    expertAuthId: string,
    startDate: string,
    endDate: string,
    availabilityType: 'date_range' | 'recurring',
    timeSlots: TimeSlot[],
    availabilityTimezone?: string
  ) => {
    try {
      setLoading(true);
      
      console.log('🔧 Creating availability with auth_id:', expertAuthId);

      // First, create the availability record
      const { data: availability, error: availabilityError } = await supabase
        .from('expert_availabilities')
        .insert({
          expert_id: expertAuthId,
          start_date: startDate,
          end_date: endDate,
          availability_type: availabilityType,
          timezone: availabilityTimezone || 'UTC'
        })
        .select()
        .single();

      if (availabilityError) throw availabilityError;

      // Then, create time slots for this availability
      // De-duplicate slots to prevent identical entries
      const uniqueMap = new Map<string, TimeSlot>();
      timeSlots.forEach(slot => {
        const key = `${slot.start_time}-${slot.end_time}-${slot.day_of_week}-${slot.specific_date || 'null'}`;
        if (!uniqueMap.has(key)) uniqueMap.set(key, slot);
      });

      const dedupedSlots = Array.from(uniqueMap.values());

      const timeSlotsToInsert = dedupedSlots.map(slot => ({
        availability_id: availability.id,
        start_time: slot.start_time,
        end_time: slot.end_time,
        day_of_week: slot.day_of_week,
        specific_date: slot.specific_date,
        timezone: slot.timezone || availabilityTimezone || 'UTC',
        is_booked: false
      }));

      const { error: slotsError } = await supabase
        .from('expert_time_slots')
        .insert(timeSlotsToInsert);

      if (slotsError) throw slotsError;

      return true;
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error('Failed to create availability');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteAvailability = async (availabilityId: string) => {
    try {
      setLoading(true);

      // Delete time slots first
      await supabase
        .from('expert_time_slots')
        .delete()
        .eq('availability_id', availabilityId);

      // Then delete availability
      const { error } = await supabase
        .from('expert_availabilities')
        .delete()
        .eq('id', availabilityId);

      if (error) throw error;

      toast.success('Availability deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error('Failed to delete availability');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailabilities = useCallback(async () => {
    // Get auth_id from user object - try auth_id first, then fallback to id
    const expertAuthId = user?.auth_id || user?.id;
    if (!expertAuthId) {
      console.log('🚫 No auth_id or id found for user:', user);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching availabilities for auth_id:', expertAuthId);

      const { data, error: fetchError } = await supabase
        .from('expert_availabilities')
        .select(`
          *,
          time_slots:expert_time_slots(*)
        `)
        .eq('expert_id', expertAuthId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      console.log('✅ Fetched availabilities:', data);
      setAvailabilities(data || []);
    } catch (err: any) {
      console.error('Error fetching availabilities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.auth_id, user?.id]);

  useEffect(() => {
    fetchAvailabilities();
  }, [fetchAvailabilities]); // Use fetchAvailabilities as dependency

  return {
    createAvailability,
    deleteAvailability,
    fetchAvailabilities,
    availabilities,
    loading,
    error
  };
}